import { useEffect, useId, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import clsx from 'clsx'
import type {
  InventoryFormModalTexts,
  InventoryFormSection,
  InventoryFormState,
  InventoryInspectionItemProvider,
  InventoryInspectionItemStatus,
  InventoryItemRow,
  PropertyDetail,
} from '@/types/custom-types'
import { useUserLanguage } from '@/customHooks'
import {
  getCatalogItemNamesForRoomType,
  getCatalogItemsForRoomType,
  isInventoryItemFilled,
  parseCatalogQuantity,
  sectionHasMinPhotos,
  type InventoryCatalogEntry,
  type InventoryCatalogRoomType,
} from '@/helpers/inventoryCatalog'
import textsData from '@/textData/InventoryMobileFlowTexts.json'
import itemTextsData from '@/textData/InventoryItemsTableTexts.json'
import { IncompleteRequiredInventoryList } from '../molecules'

const capitalizeFirstLetter = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : ''

const InventoryInspectionTypeOption = {
  Initial: 'INITIAL',
  Final: 'FINAL',
} as const

type MobileStep = 'setup' | 'rooms' | 'item' | 'summary'
type SheetKind = 'list' | 'addItem' | 'addRoom' | null

const QUALITY_OPTIONS: Array<{
  value: InventoryInspectionItemStatus
  variant: 'go' | 'warn' | 'stop'
}> = [
  { value: 'EXCELLENT', variant: 'go' },
  { value: 'GOOD', variant: 'go' },
  { value: 'REGULAR', variant: 'warn' },
  { value: 'BAD', variant: 'stop' },
]

const createLocalId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `mob-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

interface InventoryMobileFlowProps {
  title: string
  texts: InventoryFormModalTexts
  formState: InventoryFormState
  formSections: InventoryFormSection[]
  catalog: InventoryCatalogEntry[]
  property?: PropertyDetail
  isReadOnly: boolean
  canSaveInventory: boolean
  incompleteRequiredGroups?: Array<{ room: string; items: string[] }>
  saveBlockedDetail?: string | null
  isSubmitting: boolean
  onFormFieldChange: <K extends keyof InventoryFormState>(
    field: K,
    value: InventoryFormState[K],
  ) => void
  onSetSectionItems: (localId: string, items: InventoryItemRow[]) => void
  onAddSection: (section: InventoryFormSection) => void
  onSubmit: () => void
  onClose: () => void
}

const ChoiceButton = ({
  pressed,
  onClick,
  children,
  variant = 'navy',
  className,
  disabled,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
  variant?: 'navy' | 'go' | 'warn' | 'stop'
  className?: string
  disabled?: boolean
}) => {
  const pressedClass = {
    navy: 'border-[#0B3465] bg-[#0B3465] text-white',
    go: 'border-[#4A9462] bg-[#4A9462] text-white',
    warn: 'border-[#E0A021] bg-[#E0A021] text-white',
    stop: 'border-[#C0504A] bg-[#C0504A] text-white',
  }[variant]

  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex min-h-14 items-center justify-center rounded-[14px] border px-2 py-3.5 text-center text-base font-semibold',
        pressed ? pressedClass : 'border-[#E7ECF1] bg-white text-[#5A6B7D]',
        disabled && 'opacity-50',
        className,
      )}
    >
      {children}
    </button>
  )
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="mb-2 block text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#5A6B7D]">
    {children}
  </span>
)

export const InventoryMobileFlow = ({
  title,
  texts,
  formState,
  formSections,
  catalog,
  property,
  isReadOnly,
  canSaveInventory,
  incompleteRequiredGroups = [],
  saveBlockedDetail = null,
  isSubmitting,
  onFormFieldChange,
  onSetSectionItems,
  onAddSection,
  onSubmit,
  onClose,
}: InventoryMobileFlowProps) => {
  const userLanguage = useUserLanguage()
  const mobile = userLanguage === 'es' ? textsData.es : textsData.en
  const itemTexts = userLanguage === 'es' ? itemTextsData.es : itemTextsData.en
  const datalistId = useId()

  const [step, setStep] = useState<MobileStep>('setup')
  const [roomIndex, setRoomIndex] = useState(0)
  const [itemIndex, setItemIndex] = useState(0)
  const [sheet, setSheet] = useState<SheetKind>(null)
  const [showNotes, setShowNotes] = useState(false)
  const [addItemName, setAddItemName] = useState('')
  const [addRoomName, setAddRoomName] = useState('')
  const [addRoomType, setAddRoomType] = useState<InventoryCatalogRoomType>('General')
  const [newFilePreviewUrls, setNewFilePreviewUrls] = useState<string[]>([])

  const currentSection = formSections[roomIndex]
  const currentItem = currentSection?.items[itemIndex]

  useEffect(() => {
    setStep('setup')
    setRoomIndex(0)
    setItemIndex(0)
    setSheet(null)
  }, [property?.id])

  useEffect(() => {
    const created: string[] = []
    const urls = (currentItem?.newFiles ?? []).map((file) => {
      const url = URL.createObjectURL(file)
      created.push(url)
      return url
    })
    setNewFilePreviewUrls(urls)
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [currentItem?.clientId, currentItem?.newFiles])

  const progress = useMemo(() => {
    const allItems = formSections.flatMap((section) => section.items)
    const done = allItems.filter(isInventoryItemFilled).length
    return { done, total: allItems.length }
  }, [formSections])

  const address = property
    ? `${capitalizeFirstLetter(property.address?.street ?? '')} ${property.address?.buildingNumber ?? ''} · ${property.address?.city?.name ?? ''}`.trim()
    : ''
  const ownerName = property?.owner
    ? `${capitalizeFirstLetter(property.owner.firstName ?? '')} ${capitalizeFirstLetter(property.owner.lastName ?? '')}`.trim() || '—'
    : '—'
  const bedroomCount = property?.unities?.length ?? 0
  const bathroomCount =
    (property?.commonBathroom ? 1 : 0) +
    (property?.unities ?? []).filter((unity) => unity.privateBathroom).length

  const inspectionTypeLabel =
    formState.inspectionType === InventoryInspectionTypeOption.Final
      ? mobile.finalType
      : mobile.initialType

  const catalogSuggestions = getCatalogItemNamesForRoomType(
    currentSection?.catalogRoomType,
    catalog,
  )

  const patchItem = (patch: Partial<InventoryItemRow>) => {
    if (!currentSection || !currentItem || isReadOnly) return
    onSetSectionItems(
      currentSection.localId,
      currentSection.items.map((item) => {
        if (item.clientId !== currentItem.clientId) return item
        const next = { ...item, ...patch }
        next.isReviewed = isInventoryItemFilled(next)
        return next
      }),
    )
  }

  const openRoom = (index: number) => {
    const section = formSections[index]
    if (!section) return
    const firstPending = section.items.findIndex((item) => !isInventoryItemFilled(item))
    setRoomIndex(index)
    setItemIndex(firstPending >= 0 ? firstPending : 0)
    setShowNotes(false)
    setStep('item')
  }

  const handleNextItem = () => {
    if (!currentSection) return
    if (itemIndex < currentSection.items.length - 1) {
      setItemIndex((prev) => prev + 1)
      setShowNotes(false)
      return
    }
    if (!sectionHasMinPhotos(currentSection)) {
      return
    }
    setStep('rooms')
  }

  const handlePreviousItem = () => {
    if (!currentSection) return
    if (itemIndex > 0) {
      setItemIndex((prev) => prev - 1)
      setShowNotes(false)
      return
    }
    setStep('rooms')
  }

  const handleAddItem = (goToNew: boolean) => {
    if (!currentSection || isReadOnly) return
    const name = addItemName.trim()
    if (!name) return
    const newItem: InventoryItemRow = {
      clientId: createLocalId(),
      description: name,
      quantity: 1,
      status: 'NOT_EVALUATED',
      notes: '',
      isReviewed: false,
      isRequired: false,
      isPresentInProperty: 'YES',
      providedBy: null,
      existingFiles: [],
      newFiles: [],
      shouldReplaceFiles: false,
    }
    const nextItems = [...currentSection.items, newItem]
    onSetSectionItems(currentSection.localId, nextItems)
    setAddItemName('')
    if (goToNew) {
      setItemIndex(nextItems.length - 1)
      setShowNotes(false)
      setSheet(null)
      setStep('item')
    }
  }

  const handleRemoveItem = () => {
    if (!currentSection || !currentItem || isReadOnly || currentItem.isRequired) return
    const nextItems = currentSection.items.filter((item) => item.clientId !== currentItem.clientId)
    onSetSectionItems(currentSection.localId, nextItems)
    if (nextItems.length === 0) {
      setStep('rooms')
      return
    }
    setItemIndex((prev) => Math.min(prev, nextItems.length - 1))
  }

  const handleAddRoom = (withCatalog: boolean) => {
    if (isReadOnly) return
    const name = addRoomName.trim()
    if (!name) return
    const items = withCatalog
      ? getCatalogItemsForRoomType(addRoomType, catalog).map((catalogItem) => ({
          clientId: createLocalId(),
          description: catalogItem.name,
          quantity: parseCatalogQuantity(catalogItem.defaultQuantity),
          status: 'NOT_EVALUATED' as InventoryInspectionItemStatus,
          notes: '',
          isReviewed: false,
          isRequired: catalogItem.isRequired,
          isPresentInProperty: null,
          providedBy: null,
          existingFiles: [],
          newFiles: [],
          shouldReplaceFiles: false,
        }))
      : []
    onAddSection({
      localId: createLocalId(),
      key: `custom-${createLocalId()}`,
      title: name,
      isSystem: false,
      catalogRoomType: addRoomType,
      collapsed: false,
      items,
    })
    setAddRoomName('')
    setAddRoomType('General')
    setSheet(null)
  }

  const handleFilesSelected = (files: FileList | null) => {
    if (!files || !currentItem || isReadOnly) return
    const selectedFiles = Array.from(files).filter(
      (file) =>
        file.type.startsWith('image/') ||
        /\.(jpe?g|png|webp|heic|heif|gif|bmp|tiff?|avif)$/i.test(file.name),
    )
    if (selectedFiles.length === 0) return
    patchItem({
      newFiles: [...(currentItem.newFiles ?? []), ...selectedFiles],
      shouldReplaceFiles: true,
    })
  }

  const bar = (() => {
    if (step === 'setup') {
      return { title, sub: address || property?.name || '', back: true, count: null as string | null, ticks: [] as string[] }
    }
    if (step === 'rooms') {
      return {
        title: address || title,
        sub: `${inspectionTypeLabel} · ${mobile.reviewedOf
          .replace('{done}', String(progress.done))
          .replace('{total}', String(progress.total))}`,
        back: true,
        count: null,
        ticks: [] as string[],
      }
    }
    if (step === 'item' && currentSection) {
      return {
        title: currentSection.title,
        sub: `${itemIndex + 1} ${texts.progressOf} ${currentSection.items.length}${
          currentSection.catalogRoomType ? ` · ${currentSection.catalogRoomType}` : ''
        }`,
        back: true,
        count: mobile.itemList,
        ticks: currentSection.items.map((item, index) =>
          index === itemIndex ? 'here' : isInventoryItemFilled(item) ? 'done' : '',
        ),
      }
    }
    return {
      title: mobile.summaryTitle,
      sub: `${inspectionTypeLabel} · ${formState.inspectionDate}`,
      back: true,
      count: null,
      ticks: [] as string[],
    }
  })()

  const handleBack = () => {
    if (step === 'setup') {
      onClose()
      return
    }
    if (step === 'item') {
      handlePreviousItem()
      return
    }
    if (step === 'summary') {
      setStep('rooms')
      return
    }
    setStep('setup')
  }

  const summary = useMemo(() => {
    const pending: string[] = []
    const missing: string[] = []
    const damaged: string[] = []
    let ok = 0
    formSections.forEach((section) => {
      section.items.forEach((item) => {
        const tag = `${section.title} · ${item.description}`
        if (!isInventoryItemFilled(item)) pending.push(tag)
        else if (item.isPresentInProperty === 'NO') missing.push(tag)
        else if (item.status === 'BAD' || item.status === 'REGULAR') {
          damaged.push(`${tag} — ${itemTexts.statusOptions[item.status ?? 'NOT_EVALUATED']}`)
        } else ok += 1
      })
    })
    return { pending, missing, damaged, ok }
  }, [formSections, itemTexts.statusOptions])

  const fieldClass =
    'w-full rounded-[14px] border border-[#E7ECF1] bg-white px-3.5 py-3.5 text-[17px] text-[#16283D] outline-none focus:border-[#0B3465]'

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#F4F6F8]">
      <div className="sticky top-0 z-20 bg-[#0B3465] px-4 py-3 text-[#EAF1F9]">
        <div className="flex min-h-[34px] items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-lg text-white"
            aria-label={texts.buttons.back}
          >
            ←
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="m-0 truncate text-[17px] font-bold">{bar.title}</h1>
            {bar.sub ? <p className="m-0 truncate text-[12.5px] text-[#9FB6D1]">{bar.sub}</p> : null}
          </div>
          {bar.count ? (
            <button
              type="button"
              onClick={() => setSheet('list')}
              className="rounded-[10px] bg-white/15 px-3 py-2 text-sm font-semibold text-white"
            >
              {bar.count}
            </button>
          ) : null}
        </div>
        {bar.ticks.length > 0 ? (
          <div className="mt-3 flex gap-[3px]">
            {bar.ticks.map((tick, index) => (
              <span
                key={`${tick}-${index}`}
                className={clsx(
                  'h-[5px] flex-1 rounded-[3px]',
                  tick === 'done' && 'bg-[#4A9462]',
                  tick === 'here' && 'bg-white',
                  tick === '' && 'bg-white/20',
                )}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-32 pt-4">
        {step === 'setup' && (
          <div>
            <div className="mb-[18px]">
              <FieldLabel>{mobile.inspectionType}</FieldLabel>
              <p className="mb-2 text-[12.5px] text-[#93A1B0]">{mobile.inspectionTypeHint}</p>
              <div className="grid grid-cols-2 gap-2.5">
                <ChoiceButton
                  className="min-h-[78px] whitespace-pre-line text-[17px] leading-snug"
                  pressed={formState.inspectionType === InventoryInspectionTypeOption.Initial}
                  disabled={isReadOnly}
                  onClick={() =>
                    onFormFieldChange('inspectionType', InventoryInspectionTypeOption.Initial)
                  }
                >
                  {mobile.initialType}
                </ChoiceButton>
                <ChoiceButton
                  className="min-h-[78px] whitespace-pre-line text-[17px] leading-snug"
                  pressed={formState.inspectionType === InventoryInspectionTypeOption.Final}
                  disabled={isReadOnly}
                  onClick={() =>
                    onFormFieldChange('inspectionType', InventoryInspectionTypeOption.Final)
                  }
                >
                  {mobile.finalType}
                </ChoiceButton>
              </div>
            </div>

            <label className="mb-[18px] block">
              <FieldLabel>{mobile.address}</FieldLabel>
              <input className={fieldClass} value={address} readOnly />
            </label>
            <label className="mb-[18px] block">
              <FieldLabel>{mobile.owner}</FieldLabel>
              <input className={fieldClass} value={ownerName} readOnly />
            </label>
            <label className="mb-[18px] block">
              <FieldLabel>{mobile.propertyId}</FieldLabel>
              <input className={`${fieldClass} font-mono text-xs`} value={property?.id ?? ''} readOnly />
            </label>
            <label className="mb-[18px] block">
              <FieldLabel>{mobile.date}</FieldLabel>
              <input
                type="date"
                className={fieldClass}
                value={formState.inspectionDate}
                readOnly={isReadOnly}
                onChange={(event) => onFormFieldChange('inspectionDate', event.target.value)}
              />
            </label>
            <label className="mb-[18px] block">
              <FieldLabel>{mobile.inspector}</FieldLabel>
              <input
                className={fieldClass}
                value={formState.inspectorName}
                readOnly={isReadOnly}
                onChange={(event) => onFormFieldChange('inspectorName', event.target.value)}
              />
            </label>
            <label className="mb-[18px] block">
              <FieldLabel>{mobile.receiver}</FieldLabel>
              <input
                className={fieldClass}
                value={formState.receiverName}
                readOnly={isReadOnly}
                onChange={(event) => onFormFieldChange('receiverName', event.target.value)}
              />
            </label>

            <div className="mb-[18px]">
              <FieldLabel>{mobile.composition}</FieldLabel>
              <div className="rounded-[14px] border border-[#E7ECF1] bg-white p-4 shadow-[0_1px_2px_rgba(16,40,66,.04)]">
                <div className="mb-3.5 flex items-center justify-between">
                  <span className="text-sm text-[#5A6B7D]">{mobile.bedrooms}</span>
                  <span className="min-w-13 text-center text-[30px] font-extrabold tabular-nums">
                    {bedroomCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5A6B7D]">{mobile.bathrooms}</span>
                  <span className="min-w-13 text-center text-[30px] font-extrabold tabular-nums">
                    {bathroomCount}
                  </span>
                </div>
              </div>
              <p className="mt-2 text-[12.5px] text-[#93A1B0]">
                {mobile.catalogHint
                  .replace('{rooms}', String(formSections.length))
                  .replace('{items}', String(progress.total))}
              </p>
            </div>
          </div>
        )}

        {step === 'rooms' && (
          <div className="flex flex-col gap-2.5">
            {formSections.map((section, index) => {
              const filled = section.items.filter(isInventoryItemFilled).length
              const missingCount = section.items.filter((item) => item.isPresentInProperty === 'NO').length
              const badCount = section.items.filter((item) => item.status === 'BAD').length
              const hasPhoto = sectionHasMinPhotos(section)
              const done =
                section.items.length > 0 && filled === section.items.length && hasPhoto
              return (
                <button
                  key={section.localId}
                  type="button"
                  onClick={() => openRoom(index)}
                  className={clsx(
                    'block w-full rounded-[14px] border p-4 text-left shadow-[0_1px_2px_rgba(16,40,66,.04)]',
                    done
                      ? 'border-[#4A9462] bg-[#EEF5F0]'
                      : 'border-[#E7ECF1] bg-white',
                  )}
                >
                  <div className="flex items-baseline gap-2.5">
                    <span className="flex-1 text-lg font-semibold text-[#16283D]">{section.title}</span>
                    <span className="text-[13px] tabular-nums text-[#93A1B0]">
                      {filled}/{section.items.length}
                    </span>
                    {!isReadOnly ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className="shrink-0 rounded-[10px] border border-[#CFE4D7] bg-[#EEF5F0] px-2.5 py-2 text-[13px] font-bold text-[#3C7C51]"
                        onClick={(event) => {
                          event.stopPropagation()
                          setRoomIndex(index)
                          setAddItemName('')
                          setSheet('addItem')
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            event.stopPropagation()
                            setRoomIndex(index)
                            setAddItemName('')
                            setSheet('addItem')
                          }
                        }}
                      >
                        + {texts.labels.itemDescription}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex gap-[3px]">
                    {section.items.map((item) => (
                      <span
                        key={item.clientId}
                        className={clsx(
                          'h-[5px] flex-1 rounded-[3px]',
                          isInventoryItemFilled(item) ? 'bg-[#4A9462]' : 'bg-[#E7ECF1]',
                        )}
                      />
                    ))}
                  </div>
                  {(missingCount > 0 || badCount > 0 || !hasPhoto) && (
                    <div className="mt-2 text-[12.5px] font-semibold text-[#C0504A]">
                      {[
                        missingCount
                          ? (missingCount === 1 ? mobile.missingFlag : mobile.missingFlagPlural).replace(
                              '{count}',
                              String(missingCount),
                            )
                          : null,
                        badCount ? mobile.badFlag.replace('{count}', String(badCount)) : null,
                        !hasPhoto ? mobile.roomNeedsPhoto : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                </button>
              )
            })}
            {!isReadOnly ? (
              <button
                type="button"
                onClick={() => {
                  setAddRoomName('')
                  setAddRoomType('General')
                  setSheet('addRoom')
                }}
                className="flex min-h-14 items-center justify-center rounded-[14px] border border-dashed border-[#BCD6C6] bg-white text-base font-bold text-[#3C7C51]"
              >
                {mobile.addRoom}
              </button>
            ) : null}
          </div>
        )}

        {step === 'item' && currentSection && currentItem && (
          <div>
            <p className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#93A1B0]">
              {currentItem.isRequired ? (
                <span className="text-red-600">{itemTexts.requiredBadge} · </span>
              ) : null}
              {currentItem.isRequired ? mobile.catalogLabel : mobile.extraLabel}
              {currentSection.catalogRoomType ? ` · ${currentSection.catalogRoomType}` : ''}
            </p>
            <input
              list={datalistId}
              className="mt-1 w-full rounded-[14px] border border-[#E7ECF1] bg-white px-3.5 py-3 text-2xl font-extrabold tracking-[-0.02em] text-[#16283D] outline-none"
              value={currentItem.description}
              readOnly={isReadOnly}
              placeholder={itemTexts.itemPlaceholder}
              onChange={(event) => patchItem({ description: event.target.value })}
            />
            <datalist id={datalistId}>
              {catalogSuggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>

            <div className="mt-[26px]">
              <p className="mb-2.5 text-[14.5px] font-semibold text-[#5A6B7D]">{mobile.askPresent}</p>
              <div className="grid grid-cols-3 gap-2">
                <ChoiceButton
                  className="min-h-[78px] text-[16px]"
                  variant="go"
                  pressed={currentItem.isPresentInProperty === 'YES'}
                  disabled={isReadOnly}
                  onClick={() =>
                    patchItem({
                      isPresentInProperty: 'YES',
                      providedBy: null,
                      status:
                        currentItem.status === 'MISSING' ? 'NOT_EVALUATED' : currentItem.status,
                    })
                  }
                >
                  {mobile.presentYes}
                </ChoiceButton>
                <ChoiceButton
                  className="min-h-[78px] text-[16px]"
                  variant="stop"
                  pressed={currentItem.isPresentInProperty === 'NO'}
                  disabled={isReadOnly}
                  onClick={() =>
                    patchItem({
                      isPresentInProperty: 'NO',
                      quantity: '',
                      status: 'MISSING',
                    })
                  }
                >
                  {mobile.presentNo}
                </ChoiceButton>
                <ChoiceButton
                  className="min-h-[78px] px-2 text-[14px] leading-tight"
                  pressed={currentItem.isPresentInProperty === 'NOT_NEEDED'}
                  disabled={isReadOnly}
                  onClick={() =>
                    patchItem({
                      isPresentInProperty: 'NOT_NEEDED',
                      providedBy: null,
                      quantity: '',
                      status:
                        currentItem.status === 'MISSING' ? 'NOT_EVALUATED' : currentItem.status,
                    })
                  }
                >
                  {mobile.presentNotNeeded}
                </ChoiceButton>
              </div>
            </div>

            {currentItem.isPresentInProperty === 'NO' && (
              <div className="mt-[26px]">
                <p className="mb-2.5 text-[14.5px] font-semibold text-[#5A6B7D]">{mobile.askProvider}</p>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  <ChoiceButton
                    pressed={currentItem.providedBy === 'OWNER'}
                    disabled={isReadOnly}
                    onClick={() => patchItem({ providedBy: 'OWNER' as InventoryInspectionItemProvider })}
                  >
                    {itemTexts.providerOptions.OWNER}
                  </ChoiceButton>
                  <ChoiceButton
                    pressed={currentItem.providedBy === 'LIVE4LIFE'}
                    disabled={isReadOnly}
                    onClick={() =>
                      patchItem({ providedBy: 'LIVE4LIFE' as InventoryInspectionItemProvider })
                    }
                  >
                    {itemTexts.providerOptions.LIVE4LIFE}
                  </ChoiceButton>
                  <ChoiceButton
                    pressed={currentItem.providedBy === 'UNKNOWN'}
                    disabled={isReadOnly}
                    onClick={() =>
                      patchItem({ providedBy: 'UNKNOWN' as InventoryInspectionItemProvider })
                    }
                  >
                    {itemTexts.providerOptions.UNKNOWN}
                  </ChoiceButton>
                </div>
              </div>
            )}

            {currentItem.isPresentInProperty === 'YES' && (
              <>
                <div className="mt-[26px]">
                  <p className="mb-2.5 text-[14.5px] font-semibold text-[#5A6B7D]">{mobile.askQuantity}</p>
                  <div className="flex items-center gap-3.5">
                    <button
                      type="button"
                      disabled={isReadOnly}
                      className="h-14 w-14 rounded-[14px] border border-[#E7ECF1] bg-white text-[26px] font-bold"
                      onClick={() =>
                        patchItem({
                          quantity: Math.max(0, Number(currentItem.quantity || 0) - 1),
                        })
                      }
                    >
                      −
                    </button>
                    <span className="min-w-13 text-center text-[30px] font-extrabold tabular-nums">
                      {currentItem.quantity === '' ? 0 : currentItem.quantity}
                    </span>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      className="h-14 w-14 rounded-[14px] border border-[#E7ECF1] bg-white text-[26px] font-bold"
                      onClick={() =>
                        patchItem({
                          quantity: Number(currentItem.quantity || 0) + 1,
                        })
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="mt-[26px]">
                  <p className="mb-2.5 text-[14.5px] font-semibold text-[#5A6B7D]">{mobile.askQuality}</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {QUALITY_OPTIONS.map((option) => (
                      <ChoiceButton
                        key={option.value}
                        variant={option.variant}
                        pressed={currentItem.status === option.value}
                        disabled={isReadOnly}
                        onClick={() => patchItem({ status: option.value })}
                      >
                        {itemTexts.statusOptions[option.value]}
                      </ChoiceButton>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="mt-[26px]">
              <p className="mb-2.5 text-[14.5px] font-semibold text-[#5A6B7D]">
                {mobile.askPhotos}
                {(currentItem.existingFiles?.length ?? 0) + (currentItem.newFiles?.length ?? 0)
                  ? ` (${(currentItem.existingFiles?.length ?? 0) + (currentItem.newFiles?.length ?? 0)})`
                  : ''}
              </p>
              {!isReadOnly ? (
                <label className="flex min-h-14 cursor-pointer items-center justify-center gap-2.5 rounded-[14px] border border-dashed border-[#E7ECF1] bg-white text-base font-semibold text-[#5A6B7D]">
                  {mobile.takePhoto}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    className="hidden"
                    onChange={(event) => {
                      handleFilesSelected(event.target.files)
                      event.target.value = ''
                    }}
                  />
                </label>
              ) : null}
              <div className="mt-2.5 flex flex-wrap gap-2">
                {(currentItem.existingFiles ?? []).map((file) => (
                  <div key={file.id} className="relative h-[76px] w-[76px] overflow-hidden rounded-[10px]">
                    <img src={file.url} alt="" className="h-full w-full object-cover" />
                    {!isReadOnly ? (
                      <button
                        type="button"
                        className="absolute top-[3px] right-[3px] h-6 w-6 rounded-full bg-[#0B3465]/85 text-xs font-bold text-white"
                        onClick={() =>
                          patchItem({
                            existingFiles: (currentItem.existingFiles ?? []).filter(
                              (existing) => existing.id !== file.id,
                            ),
                            shouldReplaceFiles: true,
                          })
                        }
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ))}
                {newFilePreviewUrls.map((url, index) => (
                  <div key={url} className="relative h-[76px] w-[76px] overflow-hidden rounded-[10px]">
                    <img src={url} alt="" className="h-full w-full object-cover" />
                    {!isReadOnly ? (
                      <button
                        type="button"
                        className="absolute top-[3px] right-[3px] h-6 w-6 rounded-full bg-[#0B3465]/85 text-xs font-bold text-white"
                        onClick={() =>
                          patchItem({
                            newFiles: (currentItem.newFiles ?? []).filter((_, fileIndex) => fileIndex !== index),
                            shouldReplaceFiles: true,
                          })
                        }
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-[26px]">
              {!showNotes && !currentItem.notes ? (
                <button
                  type="button"
                  className="py-3.5 text-left text-[15px] font-semibold text-[#3C7C51]"
                  onClick={() => setShowNotes(true)}
                >
                  {mobile.addNotes}
                </button>
              ) : (
                <textarea
                  className={`${fieldClass} min-h-[90px]`}
                  placeholder={itemTexts.notesPlaceholder}
                  value={currentItem.notes ?? ''}
                  readOnly={isReadOnly}
                  onChange={(event) => patchItem({ notes: event.target.value })}
                />
              )}
            </div>

            {!isReadOnly ? (
              <div className="mt-[26px]">
                <button
                  type="button"
                  className="flex min-h-14 w-full items-center justify-center rounded-[14px] border border-dashed border-[#BCD6C6] bg-white text-base font-bold text-[#3C7C51]"
                  onClick={() => {
                    setAddItemName('')
                    setSheet('addItem')
                  }}
                >
                  {mobile.addItemToRoom.replace('{room}', currentSection.title)}
                </button>
                {!currentItem.isRequired ? (
                  <button
                    type="button"
                    className="w-full py-[18px] text-sm font-semibold text-[#93A1B0]"
                    onClick={handleRemoveItem}
                  >
                    {mobile.removeItemFromRoom.replace('{item}', currentItem.description || '—')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {step === 'summary' && (
          <div>
            <h2 className="m-0 text-2xl font-extrabold tracking-[-0.02em]">{address || title}</h2>
            <p className="mb-5 mt-1 text-sm text-[#5A6B7D]">
              {mobile.ownerLine
                .replace('{owner}', ownerName)
                .replace('{inspector}', formState.inspectorName || '—')
                .replace('{receiver}', formState.receiverName || '—')}
            </p>
            {property?.id ? (
              <p className="mb-4 font-mono text-[11.5px] text-[#93A1B0]">
                {mobile.propertyId}: {property.id}
              </p>
            ) : null}
            <div className="mb-[18px] flex gap-2">
              {[
                [summary.ok, mobile.tallyOk],
                [summary.damaged.length, mobile.tallyDamaged],
                [summary.missing.length, mobile.tallyMissing],
                [summary.pending.length, mobile.tallyPending],
              ].map(([value, label]) => (
                <div
                  key={String(label)}
                  className="flex-1 rounded-[14px] border border-[#E7ECF1] bg-white px-1.5 py-3 text-center"
                >
                  <b className="block text-[23px] font-extrabold tabular-nums">{value}</b>
                  <span className="text-[10.5px] font-bold uppercase tracking-wide text-[#93A1B0]">
                    {label}
                  </span>
                </div>
              ))}
            </div>
            {summary.pending.length > 0 ? (
              <SummaryCard title={`${mobile.pendingCard} (${summary.pending.length})`} tone="caution" items={summary.pending} />
            ) : null}
            {summary.missing.length > 0 ? (
              <SummaryCard title={`${mobile.missingCard} (${summary.missing.length})`} tone="alert" items={summary.missing} />
            ) : null}
            {summary.damaged.length > 0 ? (
              <SummaryCard title={`${mobile.damagedCard} (${summary.damaged.length})`} tone="caution" items={summary.damaged} />
            ) : null}
            {summary.pending.length === 0 &&
            summary.missing.length === 0 &&
            summary.damaged.length === 0 ? (
              <div className="mb-3 rounded-[14px] border border-[#CFE4D7] bg-[#EEF5F0] p-4">
                <h3 className="m-0 mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#5A6B7D]">
                  {mobile.allOkTitle}
                </h3>
                <p className="m-0 text-[14.5px] text-[#5A6B7D]">
                  {mobile.allOkBody.replace('{count}', String(summary.ok))}
                </p>
              </div>
            ) : null}
            <div className="rounded-[14px] border border-[#E7ECF1] bg-white p-4">
              <h3 className="m-0 mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#5A6B7D]">
                {texts.generalNotes}
              </h3>
              <textarea
                className={`${fieldClass} min-h-[90px]`}
                placeholder={texts.generalNotesPlaceholder}
                value={formState.generalNotes}
                readOnly={isReadOnly}
                onChange={(event) => onFormFieldChange('generalNotes', event.target.value)}
              />
            </div>
            {saveBlockedDetail || incompleteRequiredGroups.length > 0 ? (
              <div className="mt-3 text-sm font-medium text-amber-700">
                {incompleteRequiredGroups.length > 0 ? (
                  <IncompleteRequiredInventoryList
                    title={texts.incompleteRequired}
                    groups={incompleteRequiredGroups}
                    seeMoreLabel={texts.seeMoreIncomplete}
                  />
                ) : (
                  <p className="m-0 whitespace-pre-line">
                    {saveBlockedDetail || texts.saveBlockedHint}
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-linear-to-t from-[#F4F6F8] from-74% to-transparent px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3">
        {step === 'setup' && (
          <>
            {!formState.inspectorName.trim() ? (
              <p className="mb-2 text-center text-sm font-medium text-amber-700">
                {mobile.inspectorRequired}
              </p>
            ) : null}
            <button
              type="button"
              disabled={!formState.inspectorName.trim()}
              className="flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#4A9462] text-[18px] font-extrabold text-white disabled:bg-[#C3CCD4]"
              onClick={() => setStep('rooms')}
            >
              {mobile.start}
            </button>
          </>
        )}
        {step === 'rooms' && (
          <>
            <button
              type="button"
              className="flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#0B3465] text-[18px] font-extrabold text-white"
              onClick={() => setStep('summary')}
            >
              {mobile.toSummary}
            </button>
            {!isReadOnly ? (
              <button
                type="button"
                disabled={isSubmitting || !canSaveInventory}
                className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D] disabled:opacity-50"
                onClick={onSubmit}
              >
                {texts.saveInventory}
              </button>
            ) : null}
          </>
        )}
        {step === 'item' && currentSection && currentItem && (
          <>
            {itemIndex === currentSection.items.length - 1 &&
            !sectionHasMinPhotos(currentSection) ? (
              <p className="mb-2 text-center text-sm font-medium text-amber-700">
                {mobile.roomNeedsPhoto}
              </p>
            ) : null}
            <button
              type="button"
              disabled={
                !isInventoryItemFilled(currentItem) ||
                (itemIndex === currentSection.items.length - 1 &&
                  !sectionHasMinPhotos(currentSection))
              }
              className="flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#4A9462] text-[18px] font-extrabold text-white disabled:bg-[#C3CCD4]"
              onClick={handleNextItem}
            >
              {itemIndex === currentSection.items.length - 1
                ? mobile.finishRoom.replace('{room}', currentSection.title)
                : `${mobile.nextItem} →`}
            </button>
            {!isInventoryItemFilled(currentItem) ? (
              <button
                type="button"
                className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D]"
                onClick={handleNextItem}
              >
                {mobile.skipItem}
              </button>
            ) : null}
          </>
        )}
        {step === 'summary' && (
          <>
            {!isReadOnly ? (
              <button
                type="button"
                disabled={isSubmitting || !canSaveInventory}
                className="flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#0B3465] text-[18px] font-extrabold text-white disabled:bg-[#C3CCD4]"
                onClick={onSubmit}
              >
                {texts.saveInventory}
              </button>
            ) : null}
            <button
              type="button"
              className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D]"
              onClick={() => setStep('rooms')}
            >
              {mobile.backToRooms}
            </button>
          </>
        )}
      </div>

      {sheet ? (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center bg-[#0B3465]/45"
          onClick={(event) => {
            if (event.target === event.currentTarget) setSheet(null)
          }}
        >
          <div className="max-h-[82vh] w-full overflow-y-auto rounded-t-[20px] bg-[#F4F6F8] px-4 pb-[calc(20px+env(safe-area-inset-bottom))] pt-5">
            {sheet === 'list' && currentSection ? (
              <>
                <h2 className="mb-1.5 text-lg font-extrabold">
                  {currentSection.title} · {currentSection.items.filter(isInventoryItemFilled).length}{' '}
                  {texts.progressOf} {currentSection.items.length}
                </h2>
                {currentSection.items.map((item, index) => (
                  <button
                    key={item.clientId}
                    type="button"
                    className="mb-2 flex w-full items-center gap-3 rounded-xl border border-[#E7ECF1] bg-white p-3.5 text-left text-base font-semibold"
                    onClick={() => {
                      setItemIndex(index)
                      setShowNotes(false)
                      setSheet(null)
                    }}
                  >
                    <span
                      className={clsx(
                        'h-[11px] w-[11px] shrink-0 rounded-full',
                        item.isPresentInProperty === 'NO'
                          ? 'bg-[#C0504A]'
                          : item.status === 'BAD' || item.status === 'REGULAR'
                            ? 'bg-[#E0A021]'
                            : isInventoryItemFilled(item)
                              ? 'bg-[#4A9462]'
                              : 'bg-[#E7ECF1]',
                      )}
                    />
                    {item.description}
                  </button>
                ))}
              </>
            ) : null}

            {sheet === 'addItem' && currentSection ? (
              <>
                <h2 className="mb-1.5 text-lg font-extrabold">
                  {mobile.addItemTitle.replace('{room}', currentSection.title)}
                </h2>
                <p className="mb-3 text-[12.5px] text-[#93A1B0]">{texts.buttons.addItemHint}</p>
                <input
                  list={datalistId}
                  className={fieldClass}
                  value={addItemName}
                  placeholder={itemTexts.itemPlaceholder}
                  onChange={(event) => setAddItemName(event.target.value)}
                />
                <button
                  type="button"
                  className="mt-3 flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#4A9462] text-[18px] font-extrabold text-white"
                  onClick={() => handleAddItem(true)}
                >
                  {mobile.addItemSubmit}
                </button>
                <button
                  type="button"
                  className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D]"
                  onClick={() => handleAddItem(false)}
                >
                  {mobile.addItemKeepAdding}
                </button>
              </>
            ) : null}

            {sheet === 'addRoom' ? (
              <>
                <h2 className="mb-1.5 text-lg font-extrabold">{mobile.addRoomTitle}</h2>
                <p className="mb-3 text-[12.5px] text-[#93A1B0]">{mobile.addRoomHint}</p>
                <input
                  className={fieldClass}
                  value={addRoomName}
                  placeholder={texts.sectionTitlePlaceholder}
                  onChange={(event) => setAddRoomName(event.target.value)}
                />
                <div className="mt-3 grid grid-cols-3 gap-2.5">
                  {catalog.map((entry) => (
                    <ChoiceButton
                      key={entry.roomType}
                      pressed={addRoomType === entry.roomType}
                      onClick={() => setAddRoomType(entry.roomType)}
                    >
                      {entry.roomType}
                    </ChoiceButton>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-3 flex min-h-14 w-full items-center justify-center rounded-[14px] bg-[#4A9462] text-[16px] font-extrabold text-white"
                  onClick={() => handleAddRoom(true)}
                >
                  {mobile.addRoomWithCatalog}
                </button>
                <button
                  type="button"
                  className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D]"
                  onClick={() => handleAddRoom(false)}
                >
                  {mobile.addRoomEmpty}
                </button>
              </>
            ) : null}

            <button
              type="button"
              className="mt-2 flex min-h-12 w-full items-center justify-center rounded-[14px] border border-[#E7ECF1] bg-white text-[15px] font-semibold text-[#5A6B7D]"
              onClick={() => setSheet(null)}
            >
              {sheet === 'addRoom' ? mobile.cancel : mobile.closeSheet}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const SummaryCard = ({
  title,
  tone,
  items,
}: {
  title: string
  tone: 'alert' | 'caution'
  items: string[]
}) => (
  <div
    className={clsx(
      'mb-3 rounded-[14px] p-4',
      tone === 'alert'
        ? 'border border-[#EBC7C4] bg-[#FBECEB]'
        : 'border border-[#EEDCB4] bg-[#FCF4E3]',
    )}
  >
    <h3 className="m-0 mb-2.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-[#5A6B7D]">
      {title}
    </h3>
    <ul className="m-0 pl-[18px] text-[15px]">
      {items.map((item) => (
        <li key={item} className="mb-1.5 last:mb-0">
          {item}
        </li>
      ))}
    </ul>
  </div>
)
